import { ProjectSectionProps } from '../../model/ProjectSection';
import React, { ReactElement, useState } from 'react';
import { Section } from '../Section';
import { Container } from '../Container';
import styled from 'styled-components';
import { Colors } from '../../constants/colors';
import { Device } from '../../constants/device';
import { logosOnlyDeployments, standaloneDeployments } from '../../config/deploymentsConfig';
import { useTranslation } from 'react-i18next';
import { DeploymentsMap } from './DeploymentsMap';
import { useUseCase } from '../../helpers/useUseCase';
import { UseCase } from '../../config/useCasesConfig';

type Tab = 'logos' | 'map';

const extraLine: Partial<Record<UseCase, string>> = {
  transfusion: 'transfusionSites',
  testing: 'testingLabs',
};

// The first testing laboratory using slp.blue fully, named with its logo in the testing line.
const firstTestingLab = standaloneDeployments.find((deployment) => deployment.name === 'Monitoring');

/**
 * Who uses slp.blue: a map of the customers or, a click away, their logos. The
 * intro is the same for every use case; clinical laboratories also see the
 * laboratories by specialty, transfusion centres and testing laboratories a
 * line about their own.
 */
export const Deployments = (props: ProjectSectionProps): ReactElement | null => {
  const { t, i18n } = useTranslation();
  const useCase = useUseCase();
  const [tab, setTab] = useState<Tab>('map');
  const logos = [...logosOnlyDeployments, ...standaloneDeployments];

  return (
    <Section id={'deployments'} inverse={props.inverse} heading={t('section.deployments')}>
      <Container>
        <Intro dangerouslySetInnerHTML={{ __html: t('deployments.intro') }} />
        {extraLine[useCase] && (
          <ExtraLine>
            {useCase === 'testing' && firstTestingLab && (
              <LabLogo href={firstTestingLab.url} target="_blank" rel="noopener noreferrer">
                <img src={`/deployments/${firstTestingLab.photo}`} alt={firstTestingLab.name} width={56} height={56} />
              </LabLogo>
            )}
            <div dangerouslySetInnerHTML={{ __html: t(`deployments.${extraLine[useCase]}`) }} />
          </ExtraLine>
        )}
        {i18n.exists(`useCases.items.${useCase}.specialties`) && (
          <>
            <SpecialtiesIntro>{t('useCases.specialtiesIntro')}</SpecialtiesIntro>
            <Specialties>
              {(t(`useCases.items.${useCase}.specialties`, { returnObjects: true }) as string[]).map((specialty) => (
                <li key={specialty}>{specialty}</li>
              ))}
            </Specialties>
            {/* Specialties we have no count for get a sentence, as a chip without one reads as "exactly one". */}
            {i18n.exists(`useCases.items.${useCase}.moreSpecialties`) && <MoreSpecialties>{t(`useCases.items.${useCase}.moreSpecialties`)}</MoreSpecialties>}
          </>
        )}

        <Tabs>
          {(['map', 'logos'] as Tab[]).map((item) => (
            <TabButton key={item} type="button" aria-pressed={tab === item} active={tab === item} onClick={() => setTab(item)}>
              {t(`deployments.tabs.${item}`)}
            </TabButton>
          ))}
        </Tabs>
        {tab === 'logos' ? (
          <DeploymentsList>
            {logos.map((item, index) => (
              <DeploymentItem key={index} href={item.url} target="_blank">
                <img src={`/deployments/${item.photo}`} alt={item.name} />
                <DeploymentOverlay className="overlay">{item.name}</DeploymentOverlay>
              </DeploymentItem>
            ))}
          </DeploymentsList>
        ) : (
          <DeploymentsMap />
        )}
      </Container>
    </Section>
  );
};

// Renders rich text; a block container keeps the prerendered HTML and React's
// hydration in agreement on the DOM structure.
const Intro = styled.div`
  max-width: 760px;
  margin: 0 auto 30px;
  font-size: 17px;
  line-height: 1.7;
  text-align: center;
`;

// A block container, like Intro, keeps hydration in agreement on rich text.
const ExtraLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  max-width: 760px;
  margin: -14px auto 30px;
  font-size: 17px;
  text-align: center;
  color: ${Colors.primary};

  ${Device.mobile} {
    flex-direction: column;
  }
`;

const LabLogo = styled.a`
  flex-shrink: 0;
  line-height: 0;

  img {
    border-radius: 8px;
  }
`;

const SpecialtiesIntro = styled.p`
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 700;
  color: ${Colors.primary};
  /* Overrides the double spaced, left aligned on tablets paragraph styling of Section. */
  line-height: 1.5 !important;
  text-align: center !important;
`;

const Specialties = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  max-width: 820px;
  margin: 0 auto;
  padding: 0;
  list-style: none;

  li {
    padding: 3px 11px;
    border-radius: 20px;
    background: ${Colors.lighterGrey};
    font-size: 13px;
    line-height: 1.6;
  }
`;

const MoreSpecialties = styled.p`
  max-width: 760px;
  margin: 10px auto 0;
  font-size: 14px;
  line-height: 1.5 !important;
  text-align: center !important;
`;

const Tabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 45px 0 20px;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 8px 22px;
  border-radius: 20px;
  border: 2px solid ${Colors.primary};
  background: ${({ active }) => (active ? Colors.primary : 'transparent')};
  color: ${({ active }) => (active ? Colors.white : Colors.primary)};
  font: inherit;
  font-weight: 700;
  cursor: pointer;
`;

const DeploymentsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
  gap: 10px 5px;
  width: 100%;

  ${Device.tablet} {
    margin-top: 40px;
  }
`;

const DeploymentItem = styled.a`
  width: 150px;
  /* Tall enough for the hover overlay to fit the longest names on up to four lines. */
  min-height: 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  padding: 10px;

  /* Three logos per row on phones, one per row makes the list endless. */
  ${Device.mobile} {
    width: calc(33% - 5px);
    padding: 6px;
  }

  img {
    height: 30px;
    max-width: 100%;
    object-fit: contain;
    transition: opacity 0.3s ease;
  }

  &:hover img {
    opacity: 0.5;
  }

  &:hover .overlay {
    opacity: 1;
  }
`;

const DeploymentOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.8);
  color: ${Colors.primaryDark};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  opacity: 0;
  transition: opacity 0.5s ease;
  text-align: center;
  font-size: 12px;
  line-height: 1.25;
`;
